import type { LucideIcon } from 'lucide-react';

import { isRouteActive } from './activeRoute';
import { SCREENS, screenById } from './screens';

export interface NavItem {
  /** Stable id, used as a React key and for the active-item lookup. */
  id: string;
  /** What the row says. */
  label: string;
  /** The quieter second line. Null on Dashboard, which shows the plan instead. */
  description: string | null;
  icon: LucideIcon;
  route: string;
  /**
   * The products this entry is for, if any.
   *
   * <p>Absent means always shown — Dashboard, Team and My Account are backed by nothing
   * sold. Present means the row is **hidden** unless the signed-in person
   * holds **at least one** of them, matched against the keys of `products` on `/account/me`.
   * That response carried a `productKeys` array until 5 October 2026; this field keeps the
   * name because it is a property of the ROW, not of a person — see `features/team/types.ts`.
   *
   * <p>Any-of rather than all-of: the AI Agent entry is a picker in front of two agents, and
   * holding either is a reason to want the row. All-of would hide a product somebody had
   * paid for because they had not paid for its neighbour.
   *
   * <p><b>Hidden, not locked.</b> These unlocked a padlock until 24 September 2026, when
   * plans and subscriptions were cut and the padlock with them; since 3 October they decide
   * whether the row is drawn at all. The difference matters when reading older prose here: a
   * locked row was visible and said why, a hidden one says nothing.
   */
  productKeys?: readonly string[];
  /**
   * The role keys that may see this entry, if it is restricted to some.
   *
   * Absent means everyone signed in sees it, which is every row but one.
   *
   * <h2>Why this is separate from `productKeys`</h2>
   *
   * They answer different questions. A product names which endpoint the row's
   * screen calls; a role asks what kind of user this is, and comes from
   * `/account/me`. Team is backed by nothing sold — there is no Team product and
   * there should not be — so it cannot be expressed as a product at all.
   *
   * <h2>Restricted rows are HIDDEN, not padlocked</h2>
   *
   * Every locked row is deliberately still shown, because a paid product hidden
   * is invisible to the person who might buy it. That argument does not carry
   * here: a team member cannot acquire an administrator role by clicking, so a
   * padlocked Team row would only advertise something they can never have.
   *
   * <h2>And this is not security</h2>
   *
   * Hiding a menu row is a courtesy. Anyone can type the URL, so the endpoints
   * behind a restricted row are guarded on the server, exactly like the product
   * gate.
   */
  roleKeys?: readonly string[];
  /**
   * The rows nested under this one, if it is an expandable parent.
   *
   * <p>One level only. AI Agents is the single parent today and a second level of nesting
   * would be a rail nobody can scan — if one is ever wanted, it is a design decision rather
   * than a field that already allows it.
   *
   * <p>A parent keeps its own `route`: it is a link **and** an expander. Each child carries
   * its own gate, so a parent gated any-of over two products shows only the children the
   * person actually holds.
   */
  children?: readonly NavItem[];
}

/**
 * The left-hand menu.
 *
 * <h2>Why this is a list here and not rows from the server</h2>
 *
 * The menu is not the product list. **Nine top-level rows, two nested, one pinned — twelve
 * in all — against seven products, six of them fronted**, and the relationship is
 * many-to-many in both directions: Dashboard, Team and Settings & help are backed by
 * nothing sold, the AI Agents row fronts two, and `lead_meeting_intelligence` is sold with
 * no row at all.
 *
 * <p>That last one is the failure this list exists to make visible rather than one it
 * caused: `lead_meeting_intelligence` was sold from 3 October 2026 with nothing in the
 * menu to reach it.
 *
 * <p>Count them rather than trusting the number. It read "eight entries and six products
 * fronted" while there were ten top-level rows, and the removal of Product catalogue on
 * 19 October 2026 brought the row count to nine by coincidence rather than by anybody
 * recounting.
 *
 * <p>Nor is `product.name` the label. Three of the six match — Brand Intelligence, Reviews
 * & Rankings, Email Warmup — and three do not: one entry fronts two products so it can
 * match neither, and the server calls `brand_api` "Brand API" where this menu says
 * "Developer Tools". So the words, the
 * icons and the routes are here, and the API answers only what the server alone knows —
 * which products exist, and which of them the signed-in person holds. This field is one side
 * of that join and the keys of `products` on `/account/me` are the other, and
 * `AppSidebar.isVisible` makes it.
 *
 * <p>Icons are the clinching argument. A `LucideIcon` is a component; an API can
 * only send `"Mail"`, which would need a name-to-component map in this file
 * anyway — so the list would exist twice, and a name the client did not
 * recognise would fail at runtime with nothing to catch it.
 *
 * <h2>Order is display order</h2>
 *
 * Top to bottom as written. The API orders by product id, which is insertion
 * order in a catalogue and means nothing to a person reading a sidebar.
 */
/**
 * One row, built from the screen it opens.
 *
 * <p>The route, the icon, the products and the roles come from the registry, so a row and its
 * screen cannot disagree about where it goes, what it looks like or who may go there. The
 * **label** is the row's own: the rail says "Home" where the screen is "Dashboard", and
 * "Settings & help" where it is "My Account". An icon can still be overridden for a row whose
 * rail meaning differs from the screen's.
 */
function row(
  screenId: string,
  label: string,
  extras: Partial<Pick<NavItem, 'children' | 'description' | 'icon'>> = {},
): NavItem {
  const screen = screenById(screenId);

  return {
    id: screen.id,
    label,
    description: screen.description,
    icon: screen.icon,
    route: screen.route,
    ...(screen.productKeys ? { productKeys: screen.productKeys } : {}),
    ...(screen.roleKeys ? { roleKeys: screen.roleKeys } : {}),
    ...extras,
  };
}

/**
 * The left-hand menu, in the order it is drawn.
 *
 * <h2>This is a view over {@link ./screens}, not the route table</h2>
 *
 * It was both until 8 October 2026, which meant hiding a row deleted its route. See that
 * module for what went wrong and why the two are now separate. **Omitting a row here hides
 * it and nothing else**: the screen stays routable and stays guarded.
 *
 * <h2>Three screens are deliberately absent</h2>
 *
 * | Screen | Why it has no row | How it is reached |
 * |---|---|---|
 * | Lead Meeting Intelligence | the redesigned rail has no row for it | its URL only — see the registry's note |
 * | My Account | account access moved to the profile menu at the foot of the rail | that menu, and "Settings & help" |
 *
 * <p>Both are reached without a row at all. There was a third until 19 October 2026 —
 * Product catalogue, the operator's own screen, which kept a row restricted by `roleKeys`
 * because the operator has no profile menu entry to reach it from. The screen is gone and
 * so is the row.
 */
export const NAV_ITEMS: readonly NavItem[] = [
  /*
   * No pill beside it. The source carries `badge: 'Xtremand'` in its menu DATA and its
   * `renderSidebarItem` never draws it -- so the green "Xtremand" pill this row had until
   * 8 October 2026 was in neither the design nor the screen it was copied from.
   */
  row('dashboard', 'Home'),

  /*
   * The parent of the two agents, and the one row with children.
   *
   * It keeps its own route: `/agents` fronts both products and is a screen in its own right,
   * so the row is a link AND an expander. Pointing it at one of its children would choose a
   * product for somebody who may hold the other.
   *
   * Both children are hidden unless the person holds that specific agent, which is what
   * makes the parent's any-of gate and the children's single-product gates work together: a
   * person with only the SEO agent sees the parent and one child.
   */
  row('ai-agents', 'AI Agents', {
    children: [row('seo-agent', 'SEO Agent'), row('linkedin-agent', 'LinkedIn Agent')],
  }),

  row('brand-analyser', 'Brand Analyzer'),
  row('reviews', 'Reviews & Ratings'),
  row('email-warmup', 'Email Warmup'),
  row('developer-tools', 'Developer Tools'),

  // Below Developer Tools, as the redesign puts it. Not a product and never will be.
  // #used later
  // row('team', 'Team'),

  row('warmup-receivers', 'Email Warmup'),
  row('review-management', 'Review Management'),
  row('support-management', 'Support & Docs'),
  row('platform-billing', 'Billing & Plans', {
    children: [
      row('platform-billing-plans', 'Plans'),
      row('platform-billing-requests', 'Plan requests'),
    ],
  }),
] as const;

/**
 * The rows pinned to the foot of the rail, between the assistant and the profile.
 *
 * <p>Separate from {@link NAV_ITEMS} because they are laid out differently and are not part
 * of the WORKSPACE group — not because they are a different kind of thing. `isVisible`
 * applies to them identically.
 *
 * <p>**Settings & help opens `/account`**, whose own description is "Profile & settings".
 * There is no separate settings screen and this task did not invent one; when a real one is
 * built, this row points at it and nothing else changes.
 */
export const FOOTER_NAV_ITEMS: readonly NavItem[] = [
  /*
   * "Settings & help", with a lower-case h and the screen's own gear icon -- both the
   * source's, where this is `menuItems`' `my-account` entry relabelled at the point it is
   * drawn. It had a lifebuoy and a capital H until 8 October 2026, which were inventions.
   */
  row('account', 'Settings & help'),
] as const;

/**
 * The breadcrumb the top bar shows for an address.
 *
 * <p>Always rooted at "Workspace", which is the rail's one group heading — so the bar names
 * where in the menu you are rather than repeating the page's own title. A child screen reads
 * `Workspace / AI Agents / SEO Agent`, since the parent is the row that was clicked and the
 * child alone would say nothing about how it was reached.
 *
 * <p>Falls back to the screen's title for a screen with no row, and to "Home" for an address
 * that is no registered screen at all — the assistant, the profile, a collection. An empty
 * breadcrumb would collapse the bar's left half and move the controls, which reads as a
 * layout fault rather than a page without a name.
 */
export function breadcrumbFor(pathname: string): readonly string[] {
  for (const item of NAV_ITEMS) {
    if (item.route === pathname) {
      return ['Workspace', item.label];
    }

    const child = item.children?.find((candidate) => candidate.route === pathname);
    if (child) {
      return ['Workspace', item.label, child.label];
    }
  }

  const footer = FOOTER_NAV_ITEMS.find((item) => item.route === pathname);
  if (footer) {
    return ['Workspace', footer.label];
  }

  /*
   * No exact row owns this path, so it is a screen INSIDE one — `/email-warmup/create-account`
   * is inside Email Warmup. The design names the section rather than the screen here
   * (`AppNav.tsx` reads `currentPageTitle` off the active nav item), so the trail reads
   * "Workspace / Email Warmup" on every Email Warmup page, and the page's own `<h1>` is what
   * says which one. Matched with `isRouteActive`, the same rule that lights the rail row.
   */
  const section = NAV_ITEMS.find((item) => isRouteActive(item.route, pathname));
  if (section) {
    return ['Workspace', section.label];
  }

  const screen = SCREENS.find((candidate) => candidate.route === pathname);
  return ['Workspace', screen?.title ?? 'Home'];
}
